import {
    AfterViewChecked,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MultiSelectFooterTemplateDirective, MultiSelectHeaderTemplateDirective } from './multi-select-templates.directive';

@Component({
    selector: 'app-multi-select',
    templateUrl: './multi-select.component.html',
    styleUrls: ['./multi-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSelectComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class MultiSelectComponent implements ControlValueAccessor, AfterViewChecked {
    @Input() id?: string;
    @Input() placeholder?: string;

    @Input() options: any[] = [];
    /** PrimeNG default is "label" */
    @Input() optionLabel = 'label';
    /** PrimeNG: if not provided, selected values are the option objects themselves */
    @Input() optionValue?: string;

    /** PrimeNG-like filter box shown in panel */
    @Input() filter = true;
    /** Comma-separated fields; defaults to `optionLabel` */
    @Input() filterBy?: string;
    @Input() emptyMessage?: string;
    @Input() resetFilterOnHide = true;

    /** PrimeNG-like virtual scroll (for large option lists) */
    @Input() virtualScroll = false;
    @Input() virtualScrollItemSize?: number;

    /** PrimeNG-like label behavior */
    @Input() maxSelectedLabels?: number;
    /** PrimeNG-like: "{0} items selected" */
    @Input() selectedItemsLabel?: string;
    /** PrimeNG-like: max number of selections */
    @Input() selectionLimit?: number;

    /** PrimeNG-like: display selected values as "chips" with remove buttons */
    @Input() displayChips = false;

    @Input() disabled = false;
    @Input() style?: any;
    @Input() styleClass?: string;
    @Input() panelStyleClass?: string;
    @Input() showClear = false;

    /** When true, show a "Select all" checkbox in the panel header (same row as search). Default false. */
    @Input() set showSelectAll(value: boolean | string) {
        this._showSelectAll = value === true || value === 'true';
    }
    get showSelectAll(): boolean {
        return this._showSelectAll;
    }
    private _showSelectAll = false;

    /** Maintain API parity with `<p-multiSelect (onChange)=...>` */
    @Output() onChange = new EventEmitter<any>();
    @Output() onFocus = new EventEmitter<any>();
    @Output() onBlur = new EventEmitter<any>();

    @ViewChild(MatSelect) matSelect?: MatSelect;
    @ViewChild('selectAllCheckbox') selectAllCheckbox?: ElementRef<HTMLInputElement>;

    @ContentChild(MultiSelectHeaderTemplateDirective) headerTemplate?: MultiSelectHeaderTemplateDirective;
    @ContentChild(MultiSelectFooterTemplateDirective) footerTemplate?: MultiSelectFooterTemplateDirective;

    value: any[] = [];
    isOpen = false;
    filterText = '';

    private onChangeFn: (value: any) => void = () => {};
    private onTouchedFn: () => void = () => {};

    get panelClasses(): string[] {
        const base = ['app-multi-select__panel'];
        if (!this.panelStyleClass) {
            return base;
        }
        const extras = String(this.panelStyleClass)
            .split(' ')
            .map((c) => c.trim())
            .filter(Boolean);
        return [...base, ...extras];
    }

    private normalizeSelection(value: any): any[] {
        const arr = Array.isArray(value) ? value : [];
        // Treat "empty-ish" values as not selected (prevents placeholder disappearing)
        return arr.filter((v) => v !== undefined && v !== null && v !== '');
    }

    writeValue(value: any): void {
        if (Array.isArray(value)) {
            this.value = this.normalizeSelection(value);
        } else if (value === undefined || value === null || value === '') {
            this.value = [];
        } else {
            // tolerate accidental non-array values by wrapping
            this.value = [value];
        }
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    show(): void {
        this.matSelect?.open();
    }

    hide(): void {
        this.matSelect?.close();
    }

    clear(evt?: Event): void {
        if (evt) {
            evt.preventDefault();
            evt.stopPropagation();
        }
        this.onMatSelectionChange({ value: [] } as MatSelectChange);
    }

    onMatSelectionChange(event: MatSelectChange): void {
        const next = this.normalizeSelection(event?.value);
        this.value = next;
        this.onChangeFn(next);
        this.onChange.emit({ originalEvent: event, value: next });
    }

    ngAfterViewChecked(): void {
        if (this.showSelectAll && this.selectAllCheckbox?.nativeElement) {
            const el = this.selectAllCheckbox.nativeElement;
            el.checked = this.isAllDisplayedSelected;
            el.indeterminate = this.isSomeDisplayedSelected;
        }
    }

    handleOpenedChange(isOpen: boolean): void {
        this.isOpen = isOpen;
        if (isOpen) {
            setTimeout(() => {
                this.syncOverlayWidth();
            }, 0);
        }
        if (!isOpen && this.resetFilterOnHide) {
            this.filterText = '';
        }
    }

    handleFocus(evt: FocusEvent): void {
        this.onFocus.emit(evt);
    }

    handleBlur(evt: any): void {
        this.onTouchedFn();
        this.onBlur.emit(evt);
    }

    get isValueEmpty(): boolean {
        return this.normalizeSelection(this.value).length === 0;
    }

    get chipValues(): any[] {
        const selected = this.normalizeSelection(this.value);
        if (!this.displayChips || selected.length === 0) {
            return [];
        }
        const max = this.maxSelectedLabels;
        if (max === undefined || max === null) {
            return selected;
        }
        const m = Number(max);
        if (!Number.isFinite(m) || m < 0) {
            return selected;
        }
        return selected.slice(0, m);
    }

    get chipOverflowCount(): number {
        if (!this.displayChips) {
            return 0;
        }
        const selected = this.normalizeSelection(this.value);
        const max = this.maxSelectedLabels;
        if (max === undefined || max === null) {
            return 0;
        }
        const m = Number(max);
        if (!Number.isFinite(m) || m < 0) {
            return 0;
        }
        return Math.max(0, selected.length - m);
    }

    removeSelectedValue(selectedValue: any, evt?: Event): void {
        if (this.disabled) {
            return;
        }
        if (evt) {
            evt.preventDefault();
            evt.stopPropagation();
        }
        const selected = this.value ?? [];
        const next = this.normalizeSelection(selected.filter((v) => v !== selectedValue));
        this.value = next;
        this.onChangeFn(next);
        this.onChange.emit({ originalEvent: evt, value: next });
    }

    getOptionLabel(option: any): any {
        if (option === undefined || option === null) {
            return '';
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return option;
        }
        return option?.[this.optionLabel];
    }

    getOptionValue(option: any): any {
        if (option === undefined || option === null) {
            return undefined;
        }
        if (!this.optionValue) {
            return option;
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return option;
        }
        return option?.[this.optionValue];
    }

    get displayedOptions(): any[] {
        const opts = this.options ?? [];
        if (!this.filter) {
            return opts;
        }
        const q = (this.filterText || '').toLowerCase().trim();
        if (!q) {
            return opts;
        }
        const fields = (this.filterBy || this.optionLabel || 'label')
            .split(',')
            .map((f) => f.trim())
            .filter(Boolean);
        return opts.filter((o) => {
            const haystacks = fields.map((f) => String(this.getField(o, f) ?? '').toLowerCase());
            return haystacks.some((h) => h.includes(q));
        });
    }

    get emptyLabel(): string {
        return this.emptyMessage || 'No results found';
    }

    /** Displayed options that can be selected (not disabled by selectionLimit or disabled prop) */
    get selectableDisplayedOptions(): any[] {
        return (this.displayedOptions ?? []).filter((opt) => !this.isOptionDisabled(opt));
    }

    /** True when every selectable displayed option is in the current value */
    get isAllDisplayedSelected(): boolean {
        const selectable = this.selectableDisplayedOptions;
        if (selectable.length === 0) {
            return false;
        }
        const current = this.normalizeSelection(this.value);
        return selectable.every((opt) => {
            const v = this.getOptionValue(opt);
            return current.some((c) => c === v || c === opt);
        });
    }

    /** True when at least one but not all selectable displayed options are selected (for indeterminate) */
    get isSomeDisplayedSelected(): boolean {
        const selectable = this.selectableDisplayedOptions;
        if (selectable.length === 0) {
            return false;
        }
        const current = this.normalizeSelection(this.value);
        const selectedCount = selectable.filter((opt) => {
            const v = this.getOptionValue(opt);
            return current.some((c) => c === v || c === opt);
        }).length;
        return selectedCount > 0 && selectedCount < selectable.length;
    }

    toggleSelectAll(): void {
        if (this.disabled) {
            return;
        }
        const selectable = this.selectableDisplayedOptions;
        if (selectable.length === 0) {
            return;
        }
        // Defer update to next tick so UI stays responsive (avoids blocking on large lists)
        setTimeout(() => {
            let next: any[];
            if (this.isAllDisplayedSelected) {
                const displayedValues = new Set(selectable.map((opt) => this.getOptionValue(opt)));
                const current = this.normalizeSelection(this.value);
                next = current.filter((v) => !displayedValues.has(v));
            } else {
                const current = this.normalizeSelection(this.value);
                const alreadySelectedValues = new Set(current);
                const toAdd = selectable
                    .map((opt) => this.getOptionValue(opt))
                    .filter((v) => !alreadySelectedValues.has(v));
                const limit = this.selectionLimit != null && Number.isFinite(Number(this.selectionLimit)) ? Number(this.selectionLimit) : null;
                if (limit != null && limit > 0) {
                    const remaining = limit - current.length;
                    next = [...current, ...toAdd.slice(0, Math.max(0, remaining))];
                } else {
                    next = [...current, ...toAdd];
                }
            }
            this.value = next;
            this.onChangeFn(next);
            this.onChange.emit({ value: next });
        }, 0);
    }

    get itemSizePx(): number {
        return this.virtualScrollItemSize ?? 48;
    }

    get viewportHeightPx(): number {
        if (!this.virtualScroll) {
            return 0;
        }
        const maxVisible = 10;
        const visible = Math.min(this.displayedOptions.length || 0, maxVisible);
        const rows = Math.max(visible, 1);
        return rows * this.itemSizePx;
    }

    isOptionDisabled(option: any): boolean {
        if (this.disabled) {
            return true;
        }
        if (this.selectionLimit === undefined || this.selectionLimit === null) {
            return false;
        }
        const limit = Number(this.selectionLimit);
        if (!Number.isFinite(limit) || limit <= 0) {
            return false;
        }
        const optValue = this.getOptionValue(option);
        const isSelected = (this.value ?? []).some((v) => v === optValue || v === option);
        return !isSelected && (this.value?.length ?? 0) >= limit;
    }

    /** Trigger text: mimic Prime's maxSelectedLabels + selectedItemsLabel behavior */
    get triggerText(): string {
        const selected = this.normalizeSelection(this.value);
        if (selected.length === 0) {
            return '';
        }

        const labels = selected.map((v) => this.resolveLabelForSelectedValue(v)).filter((s) => String(s).length > 0);

        const max = this.maxSelectedLabels;
        if (max !== undefined && max !== null) {
            const m = Number(max);
            if (Number.isFinite(m) && m >= 0 && labels.length > m) {
                if (this.selectedItemsLabel) {
                    return this.selectedItemsLabel.replace('{0}', String(labels.length));
                }
                return `${labels.slice(0, m).join(', ')} +${labels.length - m}`;
            }
        }

        return labels.join(', ');
    }

    resolveLabelForSelectedValue(selectedValue: any): string {
        // If optionValue is provided, selectedValue is likely a primitive id, so map it to the option object.
        if (this.optionValue) {
            const match = (this.options ?? []).find((o) => this.getOptionValue(o) === selectedValue);
            if (match !== undefined) {
                return String(this.getOptionLabel(match) ?? '');
            }
        }
        // If the selected value is the option object itself
        if (selectedValue && typeof selectedValue === 'object') {
            return String(this.getOptionLabel(selectedValue) ?? '');
        }
        return String(selectedValue ?? '');
    }

    private getField(option: any, field: string): any {
        if (option === undefined || option === null) {
            return '';
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return option;
        }
        return option?.[field];
    }

    /**
     * Material sets the overlay pane width to the trigger width.
     * Ensure a minimum width so long labels don't look cramped, and allow panel to grow for long options.
     */
    private syncOverlayWidth(): void {
        const matSelectAny: any = this.matSelect as any;
        const panelEl: HTMLElement | undefined = matSelectAny?.panel?.nativeElement;
        const triggerEl: HTMLElement | undefined = matSelectAny?._elementRef?.nativeElement;
        if (!panelEl || !triggerEl) {
            return;
        }

        const pane = panelEl.closest('.cdk-overlay-pane') as HTMLElement | null;
        if (!pane) {
            return;
        }

        const triggerWidth = Math.ceil(triggerEl.getBoundingClientRect().width);
        const minWidth = 310;
        pane.style.minWidth = `${minWidth}px`;
        // If multiselect is wider than min, use same width as multiselect so both align
        pane.style.width = triggerWidth >= minWidth ? `${triggerWidth}px` : 'auto';
        panelEl.style.width = triggerWidth >= minWidth ? '100%' : 'max-content';
        panelEl.style.maxWidth = '90vw';
    }
}

